/**
 * Unified User Context - Combines analytics (main) with identity/personalization (dev)
 *
 * From main:
 * - visitorId, sessionId, experiments, trackEvent (PostHog analytics)
 * - Experiment assignment and cart attribution
 *
 * From dev:
 * - shopifyCustomerId, pageContext, identityHeaders
 * - User registration and identity resolution
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { getOrCreateVisitorId, updateCartWithExperiments } from '../utils/shopifyAttribution';
import {
  initializeUserIdentity,
  clearAllUserData,
  type UserIdentity,
} from '../utils/userIdentity';
import { detectPageContext, type PageContext } from '../utils/pageContext';
import { logger } from '../utils/logger';

// ============================================================
// Types
// ============================================================

// Session context returned by /user/identify - cached and sent with each chat request
export interface SessionContext {
  profile?: {
    id: string;
    userbio?: string;
    shopifyData?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      numberOfOrders?: number;
    };
  };
  geo?: {
    city?: string;
    region?: string;
    country?: string;
    timezone?: string;
  };
}

interface UserContextType {
  // From main (analytics)
  visitorId: string;
  sessionId: string;
  shopDomain: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  experiments: Record<string, string | boolean>;
  experimentsLoaded: boolean;
  trackEvent: (event: string, properties?: Record<string, unknown>) => void;

  // From dev (identity)
  shopifyCustomerId: string | null;
  isNewUser: boolean;
  pageContext: PageContext;

  // Session context (cached from /user/identify for chat requests)
  sessionContext: SessionContext | null;

  // New chat detection (for fresh userbio fetch)
  isNewChat: boolean;
  setIsNewChat: (value: boolean) => void;
  updateSessionProfile: (profile: Partial<SessionContext['profile']>) => void;

  // GDPR
  deleteAllData: () => Promise<void>;
}

interface UserProviderProps {
  children: ReactNode;
  apiBaseUrl: string;
}

// ============================================================
// Context
// ============================================================

const UserContext = createContext<UserContextType | null>(null);

// ============================================================
// Utilities (from main)
// ============================================================

/**
 * Generate a UUIDv7-format session ID for PostHog compatibility.
 * UUIDv7 is required for PostHog session aggregation ($session_id).
 * Format: xxxxxxxx-xxxx-7xxx-yxxx-xxxxxxxxxxxx (timestamp-based)
 */
function generateSessionId(): string {
  // Get current timestamp in milliseconds
  const timestamp = Date.now();

  // Convert to hex and pad to 12 characters (48 bits)
  const timestampHex = timestamp.toString(16).padStart(12, '0');

  // Generate random bits for the remaining parts
  const randomBytes = new Uint8Array(10);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(randomBytes);
  } else {
    // Fallback for environments without crypto
    for (let i = 0; i < randomBytes.length; i++) {
      randomBytes[i] = Math.floor(Math.random() * 256);
    }
  }

  // Build UUIDv7 format: xxxxxxxx-xxxx-7xxx-yxxx-xxxxxxxxxxxx
  // time_high (32 bits) - time_mid (16 bits) - version (4 bits) + rand_a (12 bits) - variant (2 bits) + rand_b (62 bits)
  const timeHigh = timestampHex.slice(0, 8);
  const timeMid = timestampHex.slice(8, 12);

  // Version 7 (0111) in the high nibble, then 12 random bits
  const randA = ((0x7 << 12) | (randomBytes[0] << 4) | (randomBytes[1] >> 4)).toString(16).padStart(4, '0');

  // Variant bits (10xx) in the high 2 bits, then 14 random bits
  const randB1 = ((0x8 | (randomBytes[1] & 0x3)) << 12 | (randomBytes[2] << 4) | (randomBytes[3] >> 4)).toString(16).padStart(4, '0');

  // Remaining 48 random bits
  const randB2 = Array.from(randomBytes.slice(4, 10))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');

  return `${timeHigh}-${timeMid}-${randA}-${randB1}-${randB2}`;
}

function getShopDomain(): string {
  if (typeof window !== 'undefined') {
    if ((window as any).Shopify?.shop) {
      return (window as any).Shopify.shop;
    }
    return window.location.hostname;
  }
  return 'unknown';
}

function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';

  const width = window.innerWidth;
  const userAgent = navigator.userAgent.toLowerCase();

  const isMobileUA = /mobile|android|iphone|ipod|blackberry|opera mini|iemobile/i.test(userAgent);
  const isTabletUA = /tablet|ipad|playbook|silk/i.test(userAgent);

  if (isTabletUA || (width >= 768 && width < 1024)) {
    return 'tablet';
  }
  if (isMobileUA || width < 768) {
    return 'mobile';
  }
  return 'desktop';
}

// Loader-provided data utilities (from main)
function getVisitorIdFromLoader(): string {
  if (typeof window !== 'undefined' && (window as any).CloseraConfig?._visitorId) {
    return (window as any).CloseraConfig._visitorId;
  }
  return getOrCreateVisitorId();
}

function getExperimentsFromLoader(): Record<string, string | boolean> | null {
  if (typeof window !== 'undefined' && (window as any).CloseraConfig?._experiments) {
    return (window as any).CloseraConfig._experiments;
  }
  return null;
}

// ============================================================
// Provider Component
// ============================================================

export function UserProvider({ children, apiBaseUrl }: UserProviderProps) {
  // === Main analytics state ===
  const [visitorId] = useState(() => getVisitorIdFromLoader());
  const [sessionId] = useState(() => generateSessionId());
  const [shopDomain] = useState(() => getShopDomain());
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>(() => getDeviceType());

  const loaderExperiments = getExperimentsFromLoader();
  const [experiments, setExperiments] = useState<Record<string, string | boolean>>(loaderExperiments || {});
  const [experimentsLoaded, setExperimentsLoaded] = useState(!!loaderExperiments);
  const experimentsFetched = useRef(!!loaderExperiments);

  // === Dev identity state ===
  const [identity, setIdentity] = useState<UserIdentity | null>(null);
  const [pageContext, setPageContext] = useState<PageContext>(() => detectPageContext());

  // === Session context (cached from /user/identify) ===
  const [sessionContext, setSessionContext] = useState<SessionContext | null>(null);

  // === New chat state (for fresh userbio fetch) ===
  const [isNewChat, setIsNewChat] = useState(true);

  // Configure logger with session info for error reporting
  useEffect(() => {
    logger.configure({ apiBaseUrl, visitorId, sessionId, shopDomain, deviceType });
  }, [apiBaseUrl, visitorId, sessionId, shopDomain, deviceType]);

  // Update device type on resize
  useEffect(() => {
    const handleResize = () => {
      setDeviceType(getDeviceType());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for URL changes (from dev)
  useEffect(() => {
    const handleUrlChange = () => {
      window.dispatchEvent(new Event('closera:before_navigate'));
      setPageContext(detectPageContext());
    };

    window.addEventListener('popstate', handleUrlChange);

    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);

    history.pushState = function (...args: Parameters<typeof history.pushState>) {
      const result = originalPushState(...args);
      handleUrlChange();
      return result;
    };

    history.replaceState = function (...args: Parameters<typeof history.replaceState>) {
      const result = originalReplaceState(...args);
      handleUrlChange();
      return result;
    };

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  // Initialize user identity on mount (from dev)
  useEffect(() => {
    const init = async () => {
      const userIdentity = initializeUserIdentity();
      setIdentity(userIdentity);

      // Fire and forget - registration is non-blocking
      registerUser(userIdentity, apiBaseUrl);
    };

    init();
  }, [apiBaseUrl]);

  // Register user with backend and cache session context
  const registerUser = async (
    userIdentity: UserIdentity,
    baseUrl: string
  ): Promise<void> => {
    try {
      const response = await fetch(`${baseUrl}/user/identify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          userId: userIdentity.userId,
          sessionId: userIdentity.sessionId,
          shopifyCustomerId: userIdentity.shopifyCustomerId,
          deviceType: userIdentity.deviceType,
          consentLevel: 'functional',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Cache session context for sending with chat requests
        if (data.sessionContext) {
          setSessionContext(data.sessionContext);
          logger.debug('[UserContext] Session context cached from /user/identify');
        }
      }
    } catch (error) {
      logger.error('[UserContext] Failed to register user', { errorMessage: error instanceof Error ? error.message : String(error) });
    }
  };

  // Fetch experiment assignments (from main)
  useEffect(() => {
    if (experimentsFetched.current) {
      if (loaderExperiments) {
        logger.debug('[UserContext] Using loader-provided experiments');
        updateCartWithExperiments(loaderExperiments);
      }
      return;
    }
    experimentsFetched.current = true;

    async function fetchExperiments() {
      try {
        const response = await fetch(`${apiBaseUrl}/experiment/assign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
          },
          body: JSON.stringify({
            visitorId,
            shopDomain,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          const loadedExperiments = data.experiments || {};
          setExperiments(loadedExperiments);
          logger.debug('[UserContext] Experiments loaded');
          updateCartWithExperiments(loadedExperiments);
        } else {
          logger.warn('[UserContext] Failed to fetch experiments');
          updateCartWithExperiments({});
        }
      } catch {
        logger.warn('[UserContext] Error fetching experiments');
      } finally {
        setExperimentsLoaded(true);
      }
    }

    fetchExperiments();
  }, [apiBaseUrl, visitorId, shopDomain, loaderExperiments]);

  // Track event function (from main)
  const shopifyCustomerId = identity?.shopifyCustomerId ?? null;

  const trackEvent = useCallback((event: string, properties: Record<string, unknown> = {}) => {
    fetch(`${apiBaseUrl}/track/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify({
        visitorId,
        sessionId,
        shopDomain,
        deviceType,
        event,
        properties: {
          ...properties,
          experiments,
          ...(shopifyCustomerId && { shopifyCustomerId }),
        },
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {
      logger.warn('[UserContext] Error tracking event');
    });

    logger.debug(`[UserContext] Tracked: ${event}`);
  }, [apiBaseUrl, visitorId, sessionId, shopDomain, shopifyCustomerId, deviceType, experiments]);

  // Update session profile from SSE event (for fresh userbio)
  const updateSessionProfile = useCallback((profileUpdate: Partial<SessionContext['profile']>) => {
    setSessionContext(prev => {
      // Can't update if no existing session context or profile
      if (!prev || !prev.profile) return prev;
      return {
        ...prev,
        profile: { ...prev.profile, ...profileUpdate }
      };
    });
    logger.debug('[UserContext] Profile updated from SSE');
  }, []);

  // GDPR: Delete all user data (from dev)
  const deleteAllData = useCallback(async () => {
    if (identity?.userId) {
      try {
        await fetch(`${apiBaseUrl}/user/${identity.userId}`, {
          method: 'DELETE',
        });
      } catch {
        logger.warn('Failed to delete server data');
      }
    }

    clearAllUserData();
    setIdentity(null);
  }, [identity?.userId, apiBaseUrl]);

  // Memoize context value
  const contextValue = useMemo<UserContextType>(() => ({
    // Main analytics
    visitorId,
    sessionId,
    shopDomain,
    deviceType,
    experiments,
    experimentsLoaded,
    trackEvent,

    // Dev identity
    shopifyCustomerId: identity?.shopifyCustomerId ?? null,
    isNewUser: identity?.isNewUser ?? false,
    pageContext,

    // Session context (for chat requests)
    sessionContext,

    // New chat detection
    isNewChat,
    setIsNewChat,
    updateSessionProfile,

    // GDPR
    deleteAllData,
  }), [
    visitorId,
    sessionId,
    shopDomain,
    deviceType,
    experiments,
    experimentsLoaded,
    trackEvent,
    identity?.shopifyCustomerId,
    identity?.isNewUser,
    pageContext,
    sessionContext,
    isNewChat,
    deleteAllData,
  ]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
}

// ============================================================
// Hooks
// ============================================================

export function useUser(): UserContextType {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

// From main - analytics hooks
export function useExperiments(): Record<string, string | boolean> {
  const { experiments } = useUser();
  return experiments;
}

export function useFeatureFlag(flagKey: string): string | boolean | undefined {
  const experiments = useExperiments();
  return experiments[flagKey];
}

export function useVisitorId(): string {
  const { visitorId } = useUser();
  return visitorId;
}

export function useSessionId(): string {
  const { sessionId } = useUser();
  return sessionId;
}

export function useTrackEvent(): (event: string, properties?: Record<string, unknown>) => void {
  const { trackEvent } = useUser();
  return trackEvent;
}

// From dev - identity headers hook
export function useIdentityHeaders(): Record<string, string> {
  const { visitorId, sessionId, shopifyCustomerId, pageContext } = useUser();

  const headers = useMemo(() => {
    const result: Record<string, string> = {
      'X-User-Id': visitorId,
      'X-Session-Id': sessionId,
      'X-Personalization-Consent': 'true',
      'X-Page-Context': JSON.stringify(pageContext),
    };

    if (shopifyCustomerId) {
      result['X-Shopify-Customer-Id'] = shopifyCustomerId;
    }

    return result;
  }, [visitorId, sessionId, shopifyCustomerId, pageContext]);

  return headers;
}

// Session context hook (for chat requests)
export function useSessionContext(): SessionContext | null {
  const { sessionContext } = useUser();
  return sessionContext;
}
